import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * Reads the authoritative garment count emitted by a server-rendered cart section.
 * Returns null when sections are absent or the count is not a safe non-negative integer.
 * @param {unknown} sections
 * @returns {number | null}
 */
function serverRenderedCartCount(sections) {
  if (!sections || typeof sections !== 'object' || Array.isArray(sections)) return null;

  for (const html of Object.values(sections)) {
    if (typeof html !== 'string' || !html.includes('cartItemCount')) continue;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const countNode = parsed.querySelector('[ref="cartItemCount"]');
    const rawCount = countNode?.textContent?.trim() ?? '';
    if (!/^(?:0|[1-9]\d*)$/.test(rawCount)) continue;
    const count = Number(rawCount);
    if (Number.isSafeInteger(count)) return count;
  }

  return null;
}

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    const dataCount = this.refs.cartBubbleCount.dataset.cartCount ?? '';
    const visibleCount = this.refs.cartBubbleCount.textContent ?? '';
    const rawCount = /^(?:0|[1-9]\d*)$/.test(dataCount) ? dataCount : visibleCount;
    const count = Number(rawCount);

    return Number.isSafeInteger(count) && count >= 0 ? count : 0;
  }

  set currentCartCount(value) {
    const count = Number.isSafeInteger(value) && value >= 0 ? value : 0;
    this.refs.cartBubbleCount.dataset.cartCount = String(count);
    this.refs.cartBubbleCount.textContent = count < 100 ? String(count) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const data = event.detail.data ?? {};
    if (data.didError) return;

    const absoluteServerCount = serverRenderedCartCount(data.sections);
    const itemCount = absoluteServerCount ?? data.itemCount ?? 0;
    const comingFromProductForm = absoluteServerCount === null && data.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = String(this.currentCartCount);

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
